"use client"

import { ReactNode, useCallback, useEffect, useState } from "react"
import { DialogContent, Dialog, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { VisuallyHidden } from "react-aria"
import { useRouter } from "next/navigation"
import { useAtom } from "jotai"
import {
  isFullPageModalOpenAtom,
  isSubscribeModalOpenAtom,
} from "@/app/contexts/Atoms"
import useBaseBlogUrl from "@/app/hooks/useBaseBlogUrl"
import useDismissModal from "../SubscribeModal/hooks/useDismissModal"

/**
 * This is a generic wrapper for any modal.
 *
 * This needs to be separate because:
 * 1) It's a client component
 * 2) It does an animation when it opens, so the fallback and the real body need to be
 * children of this.
 *
 * NOTE: This relies on some specific logic for blog pages, so it may not extend
 * easily for other use cases (eg, pushing to the base blog URL when dismissed).
 *
 * @param props.screenReaderTitle - The title of the modal that is hidden from the user but visible to screen readers.
 * It should describe what this page of the modal is for. If we don't include this, we get an error in the console.
 */
export default function OuterDialog(props: {
  children: ReactNode
  screenReaderTitle: string
  className?: string
}) {
  const router = useRouter()
  const baseBlogUrl = useBaseBlogUrl()
  const closeModal = useDismissModal("OuterDialog")
  const [isFullPageModalOpen, _] = useAtom(isFullPageModalOpenAtom)
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useAtom(
    isSubscribeModalOpenAtom
  )
  const [isOpen, setIsOpen] = useState(true) // Default to open once this loads.

  /**
   * For intercepting routes, ALL we need to do is
   * to push to another route and the dialog will close.
   */
  const handleClose = useCallback(
    (open: boolean) => {
      if (open) return

      closeModal()
    },
    [router, isFullPageModalOpen, isSubscribeModalOpen, baseBlogUrl]
  )

  useEffect(() => {
    if (!isFullPageModalOpen && !isSubscribeModalOpen) {
      setIsOpen(false)
    }
    router.refresh()
  }, [router, isFullPageModalOpen, isSubscribeModalOpen, baseBlogUrl])

  /**
   * For intercepting routes, this should always be open and rendered.
   *
   * If we push to a different route, it will get unmounted automatically.
   */
  return (
    <Dialog defaultOpen open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        forceMount
        className={cn(
          `h-full sm:h-auto p-0 overflow-auto sm:overflow-hidden z-[10000] sm:max-w-[800px]`,
          props.className
        )}
        // I couldn't solve focus issues with Privy modal so let's just let the success modal close
        // onInteractOutside={(e) => {
        //   // If click outside but Privy modal is open, don't close the modal
        //   if (document.getElementById("privy-modal-content")) e.preventDefault()
        // }}
      >
        <VisuallyHidden>
          <DialogTitle>{props.screenReaderTitle}</DialogTitle>
        </VisuallyHidden>
        {props.children}
      </DialogContent>
    </Dialog>
  )
}
